import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { RedirectMisnestedAreaProductToGlobal } from '@/components/RedirectMisnestedAreaProducts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/hooks/useAuth';
import { DeliveryLocationProvider } from '@/contexts/DeliveryLocationContext';
import { CartProvider } from '@/hooks/useCart';
import CitySelectionPopup from '@/components/CitySelectionPopup';
import { useCitySelectionPopup } from '@/hooks/useCitySelectionPopup';
import CityRedirector from '@/components/CityRedirector';
import LegacyUrlHandler from '@/components/LegacyUrlHandler';
import LegacyAreaRedirect from '@/components/LegacyAreaRedirect';
import CartCityValidator from '@/components/CartCityValidator';

import { useEffect, lazy, Suspense } from 'react';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

// Pages
//
// Index is eagerly imported because it's the most common entry point
// (every city home + the root path "/" land on it) AND because the
// Cloudflare bot prerender worker depends on it being in the initial
// HTML response for SEO crawlers. Every other page is lazy-loaded so
// the initial JS bundle stays small.
//
// React.lazy + Suspense doesn't break the prerender worker because
// the worker uses headless Chrome that waits for network idle — it
// downloads the lazy chunks before snapshotting the HTML. So the
// rich JSON-LD on AreaDetail / FlowerMeaningDetail / etc. still ends
// up in the rendered output for Googlebot.
import Index from '@/pages/Index';

const Auth = lazy(() => import('@/pages/Auth'));
const VendorAuth = lazy(() => import('@/pages/VendorAuth'));
const VendorEntry = lazy(() => import('@/pages/VendorEntry'));
const VendorPWAInstall = lazy(() => import('@/pages/VendorPWAInstall'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const Category = lazy(() => import('@/pages/Category'));
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));
const Occasion = lazy(() => import('@/pages/Occasion'));
const OccasionPage = lazy(() => import('@/pages/OccasionPage'));
const ProductPage = lazy(() => import('@/pages/ProductPage'));
const Products = lazy(() => import('@/pages/Products'));
const ProductsWithDelivery = lazy(() => import('@/pages/ProductsWithDelivery'));
const Flowers = lazy(() => import('@/pages/Flowers'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const OrderConfirmation = lazy(() => import('@/pages/OrderConfirmation'));
const PaymentFailure = lazy(() => import('@/pages/PaymentFailure'));
const Profile = lazy(() => import('@/pages/Profile'));
const MyOrders = lazy(() => import('@/pages/MyOrders'));
const OrderDetail = lazy(() => import('@/pages/OrderDetail'));
const TrackOrder = lazy(() => import('@/pages/TrackOrder'));
const Areas = lazy(() => import('@/pages/Areas'));
const AreaDetail = lazy(() => import('@/pages/AreaDetail'));
const CityArea = lazy(() => import('@/pages/CityArea'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Returns = lazy(() => import('@/pages/Returns'));
const Shipping = lazy(() => import('@/pages/Shipping'));
const GuidesSendFlowersEgypt = lazy(() => import('@/pages/GuidesSendFlowersEgypt'));
const FlowerMeaningsHub = lazy(() => import('@/pages/FlowerMeaningsHub'));
const FlowerMeaningDetail = lazy(() => import('@/pages/FlowerMeaningDetail'));
const RareFlowersArabic = lazy(() => import('@/pages/RareFlowersArabic'));
const FlowerColorPage = lazy(() => import('@/pages/FlowerColorPage'));
const SEOGuidePage = lazy(() => import('@/pages/SEOGuidePage'));
const FlowerTypesPage = lazy(() => import('@/pages/FlowerTypesPage'));
const CityGuidesHub = lazy(() => import('@/pages/CityGuidesHub'));
const CityGuide = lazy(() => import('@/pages/CityGuide'));
const FlowerGuide = lazy(() => import('@/pages/FlowerGuide'));
const Sitemap = lazy(() => import('@/pages/Sitemap'));
const SearchResults = lazy(() => import('@/pages/SearchResults'));
const Admin = lazy(() => import('@/pages/Admin'));
const VendorDashboard = lazy(() => import('@/pages/VendorDashboard'));
const VendorOrderDetail = lazy(() => import('@/pages/VendorOrderDetail'));
// VendorReports pulls in jsPDF (~150KB) + jspdf-autotable + html2canvas (~200KB).
// Lazy-loading it removes ~350KB of dependencies from every non-admin page load.
const VendorReports = lazy(() => import('@/pages/VendorReports'));

const Holiday = lazy(() => import('@/pages/Holiday'));
const Tips = lazy(() => import('@/pages/Tips'));
const TipDetail = lazy(() => import('@/pages/TipDetail'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const AuthRedirect = lazy(() => import('@/pages/AuthRedirect'));
// Blog components removed - redirected to blog.bouqueh.com
// Tags component removed - redirected to blog.bouqueh.com
const Pages = lazy(() => import('@/pages/Pages'));
const PageDetail = lazy(() => import('@/pages/PageDetail'));

/**
 * Generic fallback shown while a lazy route chunk is downloading.
 * Kept intentionally minimal: a thin top bar + min-height so layout
 * doesn't collapse. The shadcn Skeleton component is more polished
 * but adding it here would force it back into the main bundle.
 */
const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="h-1 w-32 bg-muted rounded-full overflow-hidden">
        <div className="h-full w-1/3 bg-primary animate-pulse rounded-full" />
      </div>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const AppContent = () => {
  const { showPopup, closePopup } = useCitySelectionPopup();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  // Scroll back to the top on every route change. Without this, SPA
  // navigation preserves the previous page's scroll position — clicking
  // "Send flowers to Giza" from the bottom of the homepage would land
  // the user at the bottom of the Giza page. Only triggers on actual
  // path changes, not query/hash updates.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // history.scrollRestoration='manual' so back/forward doesn't fight us.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  // Sync language with URL: /ar-eg/* forces 'ar', /en-eg/* forces 'en'.
  // This keeps LanguageContext, document.documentElement.lang, and body.dir
  // aligned with the route — otherwise pages can render with stale state when
  // a user navigates from /en-eg/... to /ar-eg/... without flipping the toggle.
  useEffect(() => {
    const urlLang: 'ar' | 'en' | null = location.pathname.startsWith('/ar-eg/')
      ? 'ar'
      : location.pathname.startsWith('/en-eg/')
        ? 'en'
        : null;
    if (urlLang && urlLang !== language) {
      setLanguage(urlLang);
    }
  }, [location.pathname, language, setLanguage]);

  useEffect(() => {
    document.body.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language === 'ar' ? 'ar' : 'en';
  }, [language]);

  return (
    <div>
      <CityRedirector />
      <LegacyUrlHandler />
      <CartCityValidator />
      <main id="main-content">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
        <Route path="/" element={<Index />} />
        {/* English city-prefixed routes */}
        {/* Flower-knowledge guides — order matters: more specific before less specific.
            Glossary routes must be ABOVE :topicSlug — otherwise "flower-meanings"
            would match the topic-slug placeholder and resolve to FlowerGuide. */}
        <Route path="/en-eg/guides/flower-meanings/:slug" element={<FlowerMeaningDetail />} />
        <Route path="/en-eg/guides/flower-meanings" element={<FlowerMeaningsHub />} />
        {/* SEO guide pages — explicit slugs above the :topicSlug catch-all */}
        <Route path="/en-eg/guides/flower-types" element={<FlowerTypesPage />} />
        <Route path="/en-eg/guides/birthday-flowers" element={<SEOGuidePage guideSlug="birthday-flowers" />} />
        <Route path="/en-eg/guides/engagement-flowers" element={<SEOGuidePage guideSlug="engagement-flowers" />} />
        <Route path="/en-eg/guides/wedding-flowers" element={<SEOGuidePage guideSlug="wedding-flowers" />} />
        <Route path="/en-eg/colors/:colorSlug" element={<FlowerColorPage />} />
        <Route path="/en-eg/guides/:topicSlug" element={<FlowerGuide />} />
        <Route path="/en-eg/guides" element={<GuidesSendFlowersEgypt />} />
        <Route path="/en-eg/:citySlug" element={<Index />} />
        <Route path="/en-eg/:citySlug/auth" element={<Auth />} />
        <Route path="/en-eg/:citySlug/forgot-password" element={<ForgotPassword />} />
        <Route path="/en-eg/:citySlug/category/:slug" element={<Category />} />
        <Route path="/en-eg/:citySlug/category" element={<CategoryPage />} />
        <Route path="/en-eg/:citySlug/occasion/:slug" element={<Occasion />} />
        <Route path="/en-eg/:citySlug/occasion" element={<OccasionPage />} />
        <Route path="/en-eg/area/products/:slug" element={<RedirectMisnestedAreaProductToGlobal />} />
        <Route path="/en-eg/area/products" element={<Navigate to="/en-eg/cairo/products" replace />} />
        <Route path="/en-eg/:citySlug/products/:slug" element={<ProductPage />} />
        <Route path="/en-eg/:citySlug/products" element={<Products />} />
        <Route path="/en-eg/:citySlug/products-with-delivery" element={<ProductsWithDelivery />} />
        <Route path="/en-eg/:citySlug/flowers" element={<Flowers />} />
        <Route path="/en-eg/:citySlug/checkout" element={<Checkout />} />
        <Route path="/en-eg/:citySlug/order-confirmation/:orderId" element={<OrderConfirmation />} />
        <Route path="/en-eg/:citySlug/payment-failure" element={<PaymentFailure />} />
        <Route path="/en-eg/:citySlug/profile" element={<Profile />} />
        <Route path="/en-eg/:citySlug/my-orders" element={<MyOrders />} />
        <Route path="/en-eg/:citySlug/order/:id" element={<OrderDetail />} />
        <Route path="/en-eg/:citySlug/track-order" element={<TrackOrder />} />
        <Route path="/en-eg/area/:citySlug" element={<Areas />} />
        <Route path="/en-eg/area/:citySlug/:areaSlug" element={<AreaDetail />} />
        <Route path="/en-eg/:citySlug/about" element={<About />} />
        <Route path="/en-eg/:citySlug/contact" element={<Contact />} />
        <Route path="/en-eg/:citySlug/faq" element={<FAQ />} />
        <Route path="/en-eg/:citySlug/privacy" element={<Privacy />} />
        <Route path="/en-eg/:citySlug/terms" element={<Terms />} />
        <Route path="/en-eg/:citySlug/returns" element={<Returns />} />
        <Route path="/en-eg/:citySlug/shipping" element={<Shipping />} />
        <Route path="/en-eg/:citySlug/sitemap" element={<Sitemap />} />
        <Route path="/en-eg/:citySlug/search" element={<SearchResults />} />
        <Route path="/en-eg/:citySlug/holiday/:slug" element={<Holiday />} />
        <Route path="/en-eg/:citySlug/my-orders/:orderId" element={<OrderDetail />} />
        <Route path="/en-eg/:citySlug/tips" element={<Tips />} />
        <Route path="/en-eg/:citySlug/tips/:slug" element={<TipDetail />} />
        {/* City guides — order matters: specific topic slug route before bare hub */}
        <Route path="/en-eg/:citySlug/guides/:topicSlug" element={<CityGuide />} />
        <Route path="/en-eg/:citySlug/guides" element={<CityGuidesHub />} />
        {/* Blog routes removed - redirected to blog.bouqueh.com */}
        <Route path="/en-eg/:citySlug/:areaSlug" element={<AreaDetail />} />
        {/* Arabic city-prefixed routes */}
        {/* Flower-knowledge guides — order matters: more specific before less specific */}
        <Route path="/ar-eg/guides/flower-meanings/:slug" element={<FlowerMeaningDetail />} />
        <Route path="/ar-eg/guides/flower-meanings" element={<FlowerMeaningsHub />} />
        <Route path="/ar-eg/guides/zohor-nadira" element={<RareFlowersArabic />} />
        {/* SEO guide pages — explicit slugs above the :topicSlug catch-all */}
        <Route path="/ar-eg/guides/flower-types" element={<FlowerTypesPage />} />
        <Route path="/ar-eg/guides/birthday-flowers" element={<SEOGuidePage guideSlug="birthday-flowers" />} />
        <Route path="/ar-eg/guides/engagement-flowers" element={<SEOGuidePage guideSlug="engagement-flowers" />} />
        <Route path="/ar-eg/guides/wedding-flowers" element={<SEOGuidePage guideSlug="wedding-flowers" />} />
        <Route path="/ar-eg/colors/:colorSlug" element={<FlowerColorPage />} />
        <Route path="/ar-eg/guides/:topicSlug" element={<FlowerGuide />} />
        <Route path="/ar-eg/guides" element={<GuidesSendFlowersEgypt />} />
        <Route path="/ar-eg/:citySlug" element={<Index />} />
        <Route path="/ar-eg/:citySlug/auth" element={<Auth />} />
        <Route path="/ar-eg/:citySlug/forgot-password" element={<ForgotPassword />} />
        <Route path="/ar-eg/:citySlug/category/:slug" element={<Category />} />
        <Route path="/ar-eg/:citySlug/category" element={<CategoryPage />} />
        <Route path="/ar-eg/:citySlug/occasion/:slug" element={<Occasion />} />
        <Route path="/ar-eg/:citySlug/occasion" element={<OccasionPage />} />
        <Route path="/ar-eg/area/products/:slug" element={<RedirectMisnestedAreaProductToGlobal />} />
        <Route path="/ar-eg/area/products" element={<Navigate to="/ar-eg/cairo/products" replace />} />
        <Route path="/ar-eg/:citySlug/products/:slug" element={<ProductPage />} />
        <Route path="/ar-eg/:citySlug/products" element={<Products />} />
        <Route path="/ar-eg/:citySlug/products-with-delivery" element={<ProductsWithDelivery />} />
        <Route path="/ar-eg/:citySlug/flowers" element={<Flowers />} />
        <Route path="/ar-eg/:citySlug/checkout" element={<Checkout />} />
        <Route path="/ar-eg/:citySlug/order-confirmation/:orderId" element={<OrderConfirmation />} />
        <Route path="/ar-eg/:citySlug/payment-failure" element={<PaymentFailure />} />
        <Route path="/ar-eg/:citySlug/profile" element={<Profile />} />
        <Route path="/ar-eg/:citySlug/my-orders" element={<MyOrders />} />
        <Route path="/ar-eg/:citySlug/order/:id" element={<OrderDetail />} />
        <Route path="/ar-eg/:citySlug/track-order" element={<TrackOrder />} />
        <Route path="/ar-eg/area/:citySlug" element={<Areas />} />
        <Route path="/ar-eg/area/:citySlug/:areaSlug" element={<AreaDetail />} />
        <Route path="/ar-eg/:citySlug/about" element={<About />} />
        <Route path="/ar-eg/:citySlug/contact" element={<Contact />} />
        <Route path="/ar-eg/:citySlug/faq" element={<FAQ />} />
        <Route path="/ar-eg/:citySlug/privacy" element={<Privacy />} />
        <Route path="/ar-eg/:citySlug/terms" element={<Terms />} />
        <Route path="/ar-eg/:citySlug/returns" element={<Returns />} />
        <Route path="/ar-eg/:citySlug/shipping" element={<Shipping />} />
        <Route path="/ar-eg/:citySlug/sitemap" element={<Sitemap />} />
        <Route path="/ar-eg/:citySlug/search" element={<SearchResults />} />
        <Route path="/ar-eg/:citySlug/holiday/:slug" element={<Holiday />} />
        <Route path="/ar-eg/:citySlug/my-orders/:orderId" element={<OrderDetail />} />
        <Route path="/ar-eg/:citySlug/tips" element={<Tips />} />
        <Route path="/ar-eg/:citySlug/tips/:slug" element={<TipDetail />} />
        {/* City guides — order matters: specific topic slug route before bare hub */}
        <Route path="/ar-eg/:citySlug/guides/:topicSlug" element={<CityGuide />} />
        <Route path="/ar-eg/:citySlug/guides" element={<CityGuidesHub />} />
        {/* Blog routes removed - redirected to blog.bouqueh.com */}
        <Route path="/ar-eg/:citySlug/:areaSlug" element={<AreaDetail />} />
        
        {/* Non-city-prefixed routes */}
        <Route path="/auth" element={<AuthRedirect />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/products/:slug" element={<ProductPage />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products-with-delivery" element={<ProductsWithDelivery />} />
                  <Route path="/flowers" element={<Flowers />} />
                  <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
        <Route path="/payment-failure" element={<PaymentFailure />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/my-orders" element={<MyOrders />} />
        <Route path="/order/:id" element={<OrderDetail />} />
                  <Route path="/track-order" element={<TrackOrder />} />
        {/* Legacy bare /area/* URLs (no language prefix) — 301'd at the
            Cloudflare edge via public/_redirects for SEO; these client-side
            routes redirect in-app navigation so no "Area not found" flash. */}
        <Route path="/area" element={<LegacyAreaRedirect />} />
        <Route path="/area/:citySlug" element={<LegacyAreaRedirect />} />
        <Route path="/area/:citySlug/:areaSlug" element={<LegacyAreaRedirect />} />
                  <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
        <Route path="/returns" element={<Returns />} />
                  <Route path="/shipping" element={<Shipping />} />
        <Route path="/sitemap" element={<Sitemap />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/holiday/:slug" element={<Holiday />} />
        <Route path="/tips" element={<Tips />} />
        <Route path="/tips/:slug" element={<TipDetail />} />
        {/* Blog routes removed - redirected to blog.bouqueh.com */}
        {/* Tag routes removed - redirected to blog.bouqueh.com */}
        <Route path="/pages" element={<Pages />} />
        <Route path="/pages/:slug" element={<PageDetail />} />
        <Route path="/pages/en/:slug" element={<PageDetail />} />
        <Route path="/pages/ar/:slug" element={<PageDetail />} />
        <Route path="/en-eg/tips" element={<Tips />} />
        <Route path="/en-eg/tips/:slug" element={<TipDetail />} />
        <Route path="/ar-eg/tips" element={<Tips />} />
        <Route path="/ar-eg/tips/:slug" element={<TipDetail />} />
        
        {/* Admin and vendor routes */}
        <Route path="/admin/*" element={<Admin />} />
        <Route path="/vendor-entry" element={<VendorEntry />} />
        <Route path="/vendor/install" element={<VendorPWAInstall />} />
        <Route path="/vendor/auth" element={<VendorAuth />} />
        <Route path="/vendor" element={<VendorDashboard />} />
        <Route path="/vendor/order/:orderId" element={<VendorOrderDetail />} />
        <Route path="/vendor/reports" element={<VendorReports />} />
        
        {/* Legacy URL routes for old indexed URLs */}
        <Route path="/ar" element={<Index />} />
        <Route path="/en" element={<Index />} />
        <Route path="/ar/*" element={<Index />} />
        <Route path="/en/*" element={<Index />} />
        
        {/* Catch-all route - redirect to main page */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>

      {/* City Selection Popup */}
      <CitySelectionPopup 
        isOpen={showPopup} 
        onClose={closePopup}
        language={language}
      />
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <LanguageProvider>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <AuthProvider>
                <DeliveryLocationProvider>
                  <CartProvider>
                    <AppContent />
                    <Toaster />
                  </CartProvider>
                </DeliveryLocationProvider>
              </AuthProvider>
            </TooltipProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
};

export default App;